import _ from 'lodash';
import { ActionTypes } from '../constants/AppConstants';
import AppDispatcher from '../dispatcher/AppDispatcher';
import PassengerAssistService from '../services/PassengerAssistService';
import ModalActions from './ModalActions';
import { GlobalModals } from '../constants/AppConstants';
import NotificationActions from './NotificationActions';
import UserStore from '../stores/UserStore';
import {
    ACCESSIBILITY_PROFILE_STEPS,
    BOOK_PA_STEPS,
    BOOKING_PA_STATUSES,
} from '../components/account/passengerAssist/constants';

const getAutoPopulatedAccessibilityProfileFields = () => {
    const {
        title,
        firstNames: firstName,
        surname,
        telephoneNumber: phone
    } = UserStore.getCurrent();
    return {
        title,
        firstName,
        surname,
        phone
    };
};

const PassengerAssistActions = {
    setAccessibilityProfileForm (accessibilityProfileFields) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_ACCESSIBILITY_PROFILE_FORM,
            accessibilityProfileFields
        }));
    },

    autoPopulateAccessibilityProfileForm () {
        const accessibilityProfileFields = getAutoPopulatedAccessibilityProfileFields();

        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.AUTO_POPULATE_ACCESSIBILITY_PROFILE_FORM,
            accessibilityProfileFields
        }));
    },

    resetAccessibilityProfileForm () {
        const accessibilityProfileFields = getAutoPopulatedAccessibilityProfileFields();

        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.RESET_ACCESSIBILITY_PROFILE_FORM,
            accessibilityProfileFields
        }));
    },

    setAccessibilityProfileStatus (status) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_ACCESSIBILITY_PROFILE_STATUS,
            status
        }));
    },

    setBookingPAStatus (status) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_BOOKING_PA_STATUS,
            status
        }));
    },

    setUseAccessibilityProfile (useAccessibilityProfile) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.USE_ACCESSIBILITY_PROFILE,
            useAccessibilityProfile
        }));
    },

    resetOutwardJourney () {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.RESET_OUTWARD_JOURNEY,
        }));
    },

    resetReturnJourney () {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.RESET_RETURN_JOURNEY,
        }));
    },

    setBookingPAEnterAccessibilityProfileStep (step) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_BOOKING_PA_ENTER_ACCESSIBILITY_PROFILE_STEP,
            step,
        }));
    },

    setAccessibilityProfile (accessibilityProfile) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_ACCESSIBILITY_PROFILE,
            accessibilityProfile
        }));
    },

    setNextAccessibilityProfileStep () {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_NEXT_ACCESSIBILITY_PROFILE_STEP,
        }));
    },

    setPrevAccessibilityProfileStep () {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_PREV_ACCESSIBILITY_PROFILE_STEP,
        }));
    },

    setAccessibilityProfileStep (step) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_ACCESSIBILITY_PROFILE_STEP,
            step
        }));
    },

    setNextBookingPAStep () {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_NEXT_BOOKING_PA_STEP,
        }));
    },

    setPrevBookingPAStep () {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_PREV_BOOKING_PA_STEP,
        }));
    },

    setBookingPAStep (step) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_BOOKING_PA_STEP,
            step
        }));
    },

    setTravelAssist (travelAssist) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_TRAVEL_ASSIST,
            travelAssist
        }));
    },

    deleteTravelAssist (travelAssist) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.DELETE_TRAVEL_ASSIST,
            travelAssist
        }));
    },

    setPendingTravelAssistRequests (travelAssistRequests) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_PENDING_TRAVEL_ASSIST_REQUESTS,
            travelAssistRequests
        }));
    },

    setPendingTravelAssistRequestEnabled (params) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_PENDING_TRAVEL_ASSIST_REQUEST_ENABLED,
            params
        }));
    },

    resetTravelAssist () {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.RESET_TRAVEL_ASSIST,
        }));
    },

    setIsEditPAModeEnabled (isEditPAModeEnabled) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_IS_EDIT_PA_MODE_ENABLED,
            isEditPAModeEnabled
        }));
    },

    setEditPASection (sectionIndex) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_EDIT_PA_SECTION,
            sectionIndex
        }));
    },

    setShowBackToSearchResultsButton (showButton) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_SHOW_BACK_TO_SEARCH_RESULTS_BUTTON,
            showButton
        }));
    },

    async loadCreatingAccessibilityProfileProps () {
        this.resetAccessibilityProfileForm();
        this.setAccessibilityProfileStep(ACCESSIBILITY_PROFILE_STEPS.PERSONAL_DETAILS.index);

        const accessibilityProfile = await this.getAccessibilityProfile();
        if (!accessibilityProfile) {
            this.autoPopulateAccessibilityProfileForm();
        }
    },

    async resetCreatingAccessibilityProfileProps () {
        this.setShowBackToSearchResultsButton(false);
    },

    async loadBookingPassengerAssistProps () {
        this.setUseAccessibilityProfile(true);
        this.resetAccessibilityProfileForm();
        this.setBookingPAEnterAccessibilityProfileStep(ACCESSIBILITY_PROFILE_STEPS.PERSONAL_DETAILS.index);
        this.setBookingPAStep(BOOK_PA_STEPS.PERSONAL_DETAILS.index);
        this.setBookingPAStatus(BOOKING_PA_STATUSES.BOOK_OUTWARD_JOURNEY);
        this.resetTravelAssist();
        this.setIsEditPAModeEnabled(false);

        const accessibilityProfile = await this.getAccessibilityProfile();
        this.setUseAccessibilityProfile(!!accessibilityProfile);
    },

    resetBookingPassengerAssistProps () {
        this.setUseAccessibilityProfile(true);
    },

    async getAccessibilityProfile () {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.GET_ACCESSIBILITY_PROFILE_STARTED
        }));
        try {
            const { result: accessibilityProfile } = await PassengerAssistService.getAccessibilityProfile();
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_ACCESSIBILITY_PROFILE_LOADED,
                accessibilityProfile
            }));
            return accessibilityProfile;
        } catch (err) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_ACCESSIBILITY_PROFILE_FAILED,
                err
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.passenger-assist.get-profile.failed'});
            return null;
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_ACCESSIBILITY_PROFILE_FINISHED
            }));
        }
    },

    async createAccessibilityProfile (accessibilityProfile) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.CREATE_ACCESSIBILITY_PROFILE_STARTED
        }));
        try {
            const accessibilityProfileResponse = await PassengerAssistService.createAccessibilityProfile(accessibilityProfile);
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.CREATE_ACCESSIBILITY_PROFILE_LOADED,
                accessibilityProfile: accessibilityProfileResponse
            }));
            return accessibilityProfileResponse;
        } catch (err) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.CREATE_ACCESSIBILITY_PROFILE_FAILED,
                err
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.passenger-assist.create-profile.failed'});
            return null;
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.CREATE_ACCESSIBILITY_PROFILE_FINISHED
            }));
        }
    },

    async updateAccessibilityProfile (accessibilityProfile) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.UPDATE_ACCESSIBILITY_PROFILE_STARTED
        }));
        try {
            const accessibilityProfileResponse = await PassengerAssistService.updateAccessibilityProfile(accessibilityProfile);
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.UPDATE_ACCESSIBILITY_PROFILE_LOADED,
                accessibilityProfile: accessibilityProfileResponse
            }));
            return accessibilityProfileResponse;
        } catch (err) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.UPDATE_ACCESSIBILITY_PROFILE_FAILED,
                err
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.passenger-assist.update-profile.failed'});
            return null;
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.UPDATE_ACCESSIBILITY_PROFILE_FINISHED
            }));
        }
    },

    async deleteAccessibilityProfile () {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.DELETE_ACCESSIBILITY_PROFILE_STARTED
        }));
        try {
            await PassengerAssistService.deleteAccessibilityProfile();
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.DELETE_ACCESSIBILITY_PROFILE_SUCCESS,
            }));
            NotificationActions.showNotification('notifications.accessibility-profile.deleted');
            return true;
        } catch (err) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.DELETE_ACCESSIBILITY_PROFILE_FAILED,
                err
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.passenger-assist.delete-profile.failed'});
            return false;
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.DELETE_ACCESSIBILITY_PROFILE_FINISHED
            }));
        }
    },

    async getPreviousJourneys (departureDate) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.GET_PREVIOUS_JOURNEYS_STARTED
        }));
        try {
            const previousJourneys = await PassengerAssistService.getPreviousJourneys(departureDate.format('DD/MM/YYYY'));
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_PREVIOUS_JOURNEYS_SUCCESS,
                previousJourneys,
                departureDate,
            }));
            return previousJourneys;
        } catch (err) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_PREVIOUS_JOURNEYS_FAILED,
                err
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.passenger-assist.get-previous-journeys.failed'});
            return null;
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_PREVIOUS_JOURNEYS_FINISHED
            }));
        }
    },

    async getAssistedTravelLocation (nlc) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.GET_ASSISTED_TRAVEL_LOCATION_STARTED
        }));
        try {
            const assistedTravelLocation = await PassengerAssistService.getAssistedTravelLocation(nlc);
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_ASSISTED_TRAVEL_LOCATION_SUCCESS,
                assistedTravelLocation
            }));
            return assistedTravelLocation.result.location;
        } catch (error) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_ASSISTED_TRAVEL_LOCATION_FAILED,
                error
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.passenger-assist.book-passenger-assist.failed'});
            return null;
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_ASSISTED_TRAVEL_LOCATION_FINISHED
            }));
        }
    },

    async bookAssist (passengerAssist, context) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.BOOK_ASSIST_STARTED
        }));
        try {
            const bookedAssist = await PassengerAssistService.bookAssist(passengerAssist);
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.BOOK_ASSIST_SUCCESS,
                bookedAssist,
                context
            }));
            return bookedAssist;
        } catch (err) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.BOOK_ASSIST_FAILED,
                err
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.passenger-assist.book-passenger-assist.failed'});
            return null;
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.BOOK_ASSIST_FINISHED
            }));
        }
    },

    async bookManyAssists (manyAssists) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.BOOK_MANY_ASSISTS_STARTED
        }));
        try {
            const bookedAssist = await PassengerAssistService.bookManyAssists(manyAssists);
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.BOOK_MANY_ASSISTS_SUCCESS,
                bookedAssist
            }));
            return bookedAssist;
        } catch (err) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.BOOK_MANY_ASSISTS_FAILED,
                err
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.passenger-assist.book-passenger-assist.failed'});
            return null;
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.BOOK_MANY_ASSISTS_FINISHED
            }));
        }
    },

    async getAssistedBooking (sysId) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.GET_ASSISTED_BOOKING_STARTED
        }));
        try {
            const assistedBooking = await PassengerAssistService.getAssistedBooking(sysId);
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_ASSISTED_BOOKING_SUCCESS,
                assistedBooking: assistedBooking.result.assistedTravelRequest
            }));
            return assistedBooking;
        } catch (error) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_ASSISTED_BOOKING_FAILED,
                error
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.passenger-assist.get-assisted-booking.failed'});
            return null;
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_ASSISTED_BOOKING_FINISHED
            }));
        }
    },

    async getAssistedBookings () {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.GET_ASSISTED_BOOKINGS_STARTED
        }));
        try {
            const response = await PassengerAssistService.getAssistedBookings();
            const assistedBookings = response.result.assistedTravelRequests;
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_ASSISTED_BOOKINGS_SUCCESS,
                assistedBookings
            }));
            return assistedBookings;
        } catch (error) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_ASSISTED_BOOKINGS_FAILED,
                error
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.passenger-assist.get-assisted-bookings.failed'});
            return null;
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.GET_ASSISTED_BOOKINGS_FINISHED
            }));
        }
    },

    onRequestPassengerAssistToggled (toggled) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SET_PASSENGER_ASSIST_WIDGET_TOGGLED,
            toggled
        }));
    },

    async cancelAssistedBooking (sysId) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.CANCEL_ASSISTED_BOOKING_STARTED
        }));
        try {
            await PassengerAssistService.cancelAssistedBooking(sysId);
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.CANCEL_ASSISTED_BOOKING_SUCCESS,
                sysId,
            }));
            NotificationActions.showNotification('notifications.pa-request.canceled');
        } catch (error) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.CANCEL_ASSISTED_BOOKING_FAILED,
                error
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.passenger-assist.cancel-request.failed'});
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.CANCEL_ASSISTED_BOOKING_FINISHED
            }));
        }
    },

    async editAssistedBooking (passengerAssist) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.EDIT_ASSISTED_BOOKING_STARTED
        }));
        try {
            await PassengerAssistService.editAssistedBooking(passengerAssist);
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.EDIT_ASSISTED_BOOKING_SUCCESS,
                passengerAssist,
            }));
            NotificationActions.showNotification('notifications.pa-request.updated');
        } catch (error) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.EDIT_ASSISTED_BOOKING_FAILED,
                error
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.passenger-assist.amend-request.failed'});
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.EDIT_ASSISTED_BOOKING_FINISHED
            }));
        }
    },

    clearPendingTravelAssistRequests() {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.CLEAR_PENDING_TRAVEL_ASSIST_REQUESTS,
        }));
    },

    addOrderTripIdToMostRecentPassengerAssistRequest(orderTripId) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.ADD_ORDER_TRIP_ID_TO_PASSENGER_ASSIST_REQUEST,
            orderTripId
        }));
    },
};

export default PassengerAssistActions;
